import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, ArrowLeft } from 'lucide-react';
import CardBasic from '../../ui/Card.jsx';
import NoteTemplatesTable from './NoteTemplatesTable.jsx';
import NoteTemplateCreateModal from './NoteTemplateCreateModal.jsx';
import NoteTemplateEditModal from './NoteTemplateEditModal.jsx';
import NoteTemplateDeleteModal from './NoteTemplateDeleteModal.jsx';
import { showMessage } from '../../../scripts/showMessage.js';

// Action Bar Styles
const actionBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    background: 'var(--bg-secondary, #f5f7f9)',
    borderBottom: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '12px 12px 0 0'
};

const backButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-primary, #2c3e50)',
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const NoteTemplatesIndex = () => {
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Fetch all templates
    const fetchTemplates = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch('/index.php/notestemplates/getAllNoteTemplatesJSON');
            if (!response.ok) throw new Error('Failed to fetch templates');
            const data = await response.json();
            setTemplates(data);
        } catch (error) {
            console.error('Error fetching templates:', error);
            showMessage('Fehler beim Laden der Notizvorlagen', 'error');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    // Handle edit
    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setShowEditModal(true);
    };

    // Handle delete
    const handleDelete = (template) => {
        setSelectedTemplate(template);
        setShowDeleteModal(true);
    };

    // Handle create success
    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        fetchTemplates();
        showMessage('Notizvorlage erfolgreich erstellt', 'success');
    };

    // Handle edit success
    const handleEditSuccess = () => {
        setShowEditModal(false);
        setSelectedTemplate(null);
        fetchTemplates();
        showMessage('Notizvorlage erfolgreich aktualisiert', 'success');
    };

    // Handle delete success
    const handleDeleteSuccess = () => {
        setShowDeleteModal(false);
        setSelectedTemplate(null);
        fetchTemplates();
        showMessage('Notizvorlage erfolgreich gelöscht', 'success');
    };

    return (
        <div>
            {/* Page Header */}
            <div className="mb-4">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div>
                        <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                            <FileText size={32} className="icon-settings" />
                            Notizvorlagen
                        </h1>
                        <p className="text-muted mb-0">
                            Erstellen und verwalten Sie Vorlagen für Notizen in verschiedenen Bereichen.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn btn-outline-primary d-flex align-items-center gap-2 shadow"
                    >
                        <Plus size={20} />
                        Neue Vorlage
                    </button>
                </div>
            </div>

            {/* Table Card */}
            <CardBasic style={{ padding: 0, overflow: 'hidden' }}>
                {/* Action Bar */}
                <div style={actionBarStyle}>
                    <a href="index.php/settings" style={backButtonStyle}>
                        <ArrowLeft size={18} />
                        Zurück
                    </a>
                </div>
                <NoteTemplatesTable
                    templates={templates}
                    isLoading={isLoading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            </CardBasic>

            {/* Create Modal */}
            {showCreateModal && (
                <NoteTemplateCreateModal
                    show={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleCreateSuccess}
                />
            )}

            {/* Edit Modal */}
            {showEditModal && selectedTemplate && (
                <NoteTemplateEditModal
                    show={showEditModal}
                    template={selectedTemplate}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedTemplate(null);
                    }}
                    onSuccess={handleEditSuccess}
                />
            )}

            {/* Delete Modal */}
            {showDeleteModal && selectedTemplate && (
                <NoteTemplateDeleteModal
                    show={showDeleteModal}
                    template={selectedTemplate}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedTemplate(null);
                    }}
                    onSuccess={handleDeleteSuccess}
                />
            )}
        </div>
    );
};

export default NoteTemplatesIndex;
